/**
 * The only entry for server frames and frame patches. Each API frame or
 * patch is split into data (frames store, behind the local edit guard) and
 * geometry (canvas store, behind the local drag and focus guards). New
 * frames hand their placement to the canvas node subscription in the same
 * synchronous call.
 */
import { isNotFoundError } from "@/api/client";
import { handleApiError } from "@/api/error-middleware";
import {
  getFrame,
  getUserFrame,
  isFrameId,
  type FrameSummary,
} from "@/api/frame-listing";
import type { Frame } from "@/api/spaces";
import {
  REGISTERED_FRAME_TYPES,
  frameDataFromApi,
  frameDataFromSummary,
  frameDataPatchFromApi,
  isNewerServerTime,
  type FrameData,
} from "@/lib/frame-data";
import {
  ensureFrameDetail,
  isCardOnlyFrame,
} from "@/lib/frame-detail-requests";
import { stopFrameTerminal } from "@/lib/frame-visibility";
import {
  holdsDeferredEmit,
  withMutationEmitSuppressed,
  withStoreKeptOnMirror,
} from "@/lib/mirrored-store";
import {
  FRAME_GEOMETRY_KEYS,
  framePlacementOf,
  withFrameGeometry,
  type FrameGeometryPatch,
} from "@/stores/canvas-frame-geometry";
import { retainedFocusFrameIds } from "@/stores/canvas-frame-load";
import { placeFrameNodes } from "@/stores/canvas-frame-nodes";
import { useCanvasStore } from "@/stores/canvas-store";
import {
  usePresentationStore,
  type PresentationStoreState,
} from "@/stores/presentation-store";
import { protectLocalFrameEdit } from "@/stores/frame-edit-guard";
import {
  clearLocalFrameGeometry,
  protectLocalFrameGeometry,
} from "@/stores/frame-geometry-guard";
import { useFramesStore, type FramePatchEntry } from "@/stores/frames-store";
import { useProjectStore } from "@/stores/project-store";
import { isWallActive } from "@/stores/workspace-view-store";

type FrameGeometryEntry = [id: string, geometry: FrameGeometryPatch];

/** A frame shown by focus keeps the presenting layout's geometry. */
function keepFocusedGeometry(
  state: Pick<PresentationStoreState, "presentation">,
  frameId: string,
  geometry: FrameGeometryPatch,
): FrameGeometryPatch {
  return state.presentation?.variant === "focus" &&
    state.presentation.frameIds.includes(frameId)
    ? {}
    : geometry;
}

function geometryOf(patch: Partial<Frame>): FrameGeometryPatch {
  const geometry: FrameGeometryPatch = {};
  for (const key of FRAME_GEOMETRY_KEYS) {
    if (patch[key] !== undefined) geometry[key] = patch[key];
  }
  return geometry;
}

function applyFrameGeometry(entries: readonly FrameGeometryEntry[]): void {
  if (entries.length === 0) return;
  const byId = new Map(entries);
  useCanvasStore.setState((state) => {
    let changed = false;
    const nodes = state.nodes.map((node) => {
      const geometry = byId.get(node.id);
      if (!geometry) return node;
      const next = withFrameGeometry(node, geometry);
      if (next !== node) changed = true;
      return next;
    });
    return changed ? { nodes } : state;
  });
}

/**
 * Who wrote a patch. A server payload is the stored frame (or part of it); a
 * local write is derived from the content this window holds.
 */
export type FramePatchOrigin = "server" | "local";

let cardWriteReported = false;

/** Development throws; release logs once. Called after the rest of a batch landed. */
function reportCardContentWrites(frameIds: readonly string[]): void {
  if (frameIds.length === 0) return;
  const message = `Refused a content write to card-only frame ${frameIds.join(", ")}; load its detail first.`;
  if (import.meta.env.DEV) throw new Error(message);
  if (cardWriteReported) return;
  cardWriteReported = true;
  console.error(`[frame-ingest] ${message}`);
}

/**
 * True when a local content write targets a card-only frame, whose `content`
 * is empty until its detail loads: writing it would overwrite the stored
 * frame with a partial copy. The caller skips the write.
 */
export function refusesCardContentWrite(frameId: string): boolean {
  if (!isCardOnlyFrame(frameId)) return false;
  reportCardContentWrites([frameId]);
  return true;
}

/** The edit guard kept newer local content: the server's card describes the older one. */
function withoutStaleCard(
  patch: Partial<Frame>,
  guarded: Partial<Frame>,
): Partial<Frame> {
  if (
    patch.content === undefined ||
    guarded.content !== undefined ||
    guarded.card === undefined
  ) {
    return guarded;
  }
  const { card: _staleCard, ...rest } = guarded;
  return rest;
}

/**
 * Content arriving for a card-only frame. A local write is refused. A server
 * payload with its `card` is the whole stored content and makes the frame
 * full; one without it may be a partial delta, so its content is dropped and
 * the detail loads instead.
 */
function cardFrameContent(
  patch: Partial<Frame>,
  origin: FramePatchOrigin,
): "apply" | "refuse" | "load-detail" {
  if (origin === "local") return "refuse";
  return patch.card !== undefined ? "apply" : "load-detail";
}

function applyFramePatches(
  entries: readonly (readonly [string, Partial<Frame>])[],
  origin: FramePatchOrigin,
): void {
  const presentation = usePresentationStore.getState();
  const data: FramePatchEntry[] = [];
  const geometry: FrameGeometryEntry[] = [];
  const refused: string[] = [];
  const loadDetail: string[] = [];
  for (const [id, patch] of entries) {
    let incoming = patch;
    if (patch.content !== undefined && isCardOnlyFrame(id)) {
      const action = cardFrameContent(patch, origin);
      if (action === "refuse") {
        refused.push(id);
        continue;
      }
      if (action === "load-detail") {
        const { content: _partialContent, ...rest } = patch;
        incoming = rest;
        loadDetail.push(id);
      }
    }
    const guarded = protectLocalFrameGeometry(
      id,
      withoutStaleCard(incoming, protectLocalFrameEdit(id, incoming)),
    );
    data.push([id, frameDataPatchFromApi(guarded)]);
    const moved = keepFocusedGeometry(presentation, id, geometryOf(guarded));
    if (Object.keys(moved).length > 0) geometry.push([id, moved]);
  }
  useFramesStore.getState().patchFrames(data);
  applyFrameGeometry(geometry);
  // A failed detail has shown its toast; the frame stays card-only.
  for (const id of loadDetail) void ensureFrameDetail(id).catch(() => {});
  reportCardContentWrites(refused);
}

/**
 * The wall's full list: unknown frames arrive with their canvas node, cards
 * become full, and the other frames stay. A frame a websocket event made
 * newer while the list loaded keeps that version.
 */
function mergeFrames(loaded: readonly Frame[]): void {
  const { framesById } = useFramesStore.getState();
  const merged = loaded.filter((frame) => {
    const knownAt = framesById[frame.id]?.updatedAt;
    return !knownAt || !isNewerServerTime(knownAt, frame.updated_at);
  });
  placeFrameNodes(
    new Map(merged.map((frame) => [frame.id, framePlacementOf(frame)])),
    { load: false },
    () => useFramesStore.getState().upsertFrames(merged.map(frameDataFromApi)),
  );
}

/**
 * Ingest a full server list. Streaming frames load as done and unregistered
 * types stay out. On the wall the list merges into the loaded frames (cards
 * from summaries stay; a project switch clears first). On the canvas it
 * replaces every frame, keeping the temporary conversation frames a running
 * focus presentation shows.
 */
export function ingestFrames(frames: readonly Frame[]): void {
  const loaded = frames
    .filter((frame) => REGISTERED_FRAME_TYPES.has(frame.frame_type))
    .map((frame) =>
      frame.status === "streaming"
        ? { ...frame, status: "done" as const }
        : frame,
    );
  if (isWallActive()) {
    mergeFrames(loaded);
    return;
  }
  clearLocalFrameGeometry();
  const loadedSpaceId =
    loaded[0]?.project ??
    loaded[0]?.space ??
    useProjectStore.getState().currentSpace?.id;
  const { framesById } = useFramesStore.getState();
  const retained = retainedFocusFrameIds(
    useCanvasStore.getState(),
    new Set(loaded.map((frame) => frame.id)),
    loadedSpaceId,
  ).flatMap((id) => (framesById[id] ? [framesById[id]] : []));
  placeFrameNodes(
    new Map(loaded.map((frame) => [frame.id, framePlacementOf(frame)])),
    { load: true },
    () =>
      useFramesStore
        .getState()
        .replaceFrames([...loaded.map(frameDataFromApi), ...retained]),
  );
}

/** Add one server frame with its canvas node; a known frame is left as is. */
export function ingestFrame(frame: Frame): void {
  if (!REGISTERED_FRAME_TYPES.has(frame.frame_type)) return;
  if (useFramesStore.getState().framesById[frame.id]) return;
  placeFrameNodes(
    new Map([[frame.id, framePlacementOf(frame)]]),
    { load: false },
    () => useFramesStore.getState().upsertFrame(frameDataFromApi(frame)),
  );
}

/**
 * A frame by id: the loaded one, else fetched and added in full (a wall
 * page never brought it). Null for a type this app does not render, or
 * when the project changed while it loaded.
 */
export async function loadFrame(
  projectId: string,
  frameId: string,
): Promise<FrameData | null> {
  const known = useFramesStore.getState().framesById[frameId];
  if (known) return known;
  const frame = await getFrame(projectId, frameId);
  // The project changed while it loaded: the frame is not this one's.
  if (useProjectStore.getState().currentProject?.id !== projectId) return null;
  // A mirror's canvas node stays local; the frame reaches the owner by id.
  withStoreKeptOnMirror("canvas", () => ingestFrame(frame));
  return useFramesStore.getState().framesById[frameId] ?? null;
}

/**
 * A frame by id from whichever of the user's projects holds it: the loaded
 * one, else fetched and added in full. One of another project is held in
 * the store for the presentation showing it; the wall lists only the viewed
 * project's cards. Null for a type this app does not render.
 */
async function loadAnyProjectFrame(frameId: string): Promise<FrameData | null> {
  const known = useFramesStore.getState().framesById[frameId];
  if (known) return known;
  const frame = await getUserFrame(frameId);
  withStoreKeptOnMirror("canvas", () => ingestFrame(frame));
  return useFramesStore.getState().framesById[frameId] ?? null;
}

type UnpagedLoad = "loaded" | "missing" | "failed";

/** Fetches by id in flight, per project (or any project) and frame. */
const unpagedLoads = new Map<string, Promise<UnpagedLoad>>();

/** `projectId` null: whichever of the user's projects holds the frame. */
function loadUnpagedFrame(
  projectId: string | null,
  frameId: string,
): Promise<UnpagedLoad> {
  const key = `${projectId ?? "*"}/${frameId}`;
  const inFlight = unpagedLoads.get(key);
  if (inFlight) return inFlight;
  const load = (
    projectId ? loadFrame(projectId, frameId) : loadAnyProjectFrame(frameId)
  )
    .then(
      (frame): UnpagedLoad => (frame ? "loaded" : "missing"),
      (error: unknown): UnpagedLoad => {
        if (isNotFoundError(error)) return "missing";
        handleApiError(error);
        return "failed";
      },
    )
    .finally(() => unpagedLoads.delete(key));
  unpagedLoads.set(key, load);
  return load;
}

/** The plain ids among `frameIds` this window does not hold. */
function unheldFrameIds(frameIds: readonly string[]): string[] {
  const { framesById } = useFramesStore.getState();
  return [...new Set(frameIds)].filter(
    (id) => !framesById[id] && isFrameId(id),
  );
}

/**
 * Fetch by id the frames a presentation names that this window does not
 * hold, each from whichever of the user's projects holds it: Denker shows
 * cards of any project, not only the viewed one's. A frame that is not
 * found stays missing; another failure shows the API error toast.
 */
export async function loadPresentedFrames(
  frameIds: readonly string[],
): Promise<void> {
  await Promise.all(
    unheldFrameIds(frameIds).map((id) => loadUnpagedFrame(null, id)),
  );
}

export interface UnpagedFramesLoad {
  /** False when the project changed while they loaded. */
  current: boolean;
  /** Ids whose fetch failed; the API error toast has shown. */
  failed: ReadonlySet<string>;
}

/**
 * Fetch by id the frames the wall has not paged in, one request per frame
 * however many callers wait. A frame that is not found, or whose id is not
 * a plain id, stays missing.
 */
export async function loadUnpagedFrames(
  frameIds: readonly string[],
): Promise<UnpagedFramesLoad> {
  const projectId = useProjectStore.getState().currentProject?.id;
  const missing = unheldFrameIds(frameIds);
  const failed = new Set<string>();
  if (projectId) {
    await Promise.all(
      missing.map(async (id) => {
        if ((await loadUnpagedFrame(projectId, id)) === "failed") {
          failed.add(id);
        }
      }),
    );
  }
  return {
    current: useProjectStore.getState().currentProject?.id === projectId,
    failed,
  };
}

/**
 * Add frames known only from the summary listing (`content: {}`, `"card"`).
 * A full frame is never downgraded (websocket events keep it current), and a
 * card frame is replaced only when the server changed it. Archiving keeps
 * `updated_at`, so a changed `archived_at` is applied to any known frame. No
 * canvas node is placed: a summary carries no geometry. A page stays in the
 * window that loaded it (every window pages its own wall), so no other
 * window is told.
 */
export function ingestSummaries(
  summaries: readonly FrameSummary[],
  projectId: string,
): void {
  const { framesById, upsertFrames, patchFrames } = useFramesStore.getState();
  const added: FrameData[] = [];
  const archiveChanges: FramePatchEntry[] = [];
  for (const summary of summaries) {
    if (!REGISTERED_FRAME_TYPES.has(summary.frame_type)) continue;
    const known = framesById[summary.id];
    if (
      !known ||
      (known.contentState === "card" &&
        isNewerServerTime(summary.updated_at, known.updatedAt))
    ) {
      added.push(frameDataFromSummary(summary, projectId));
    } else if ((known.archivedAt ?? null) !== summary.archived_at) {
      archiveChanges.push([summary.id, { archivedAt: summary.archived_at }]);
    }
  }
  withMutationEmitSuppressed(() => {
    upsertFrames(added);
    patchFrames(archiveChanges);
  });
}

/**
 * Apply a patch now. `origin` defaults to a local write; a server response
 * (a saved or fetched frame) passes `"server"`.
 */
export function applyFramePatch(
  id: string,
  patch: Partial<Frame>,
  { origin = "local" }: { origin?: FramePatchOrigin } = {},
): void {
  applyFramePatches([[id, patch]], origin);
}

const pendingPatches = new Map<string, Partial<Frame>>();
/** Queued on a mirror while applying a frame: applied without emitting. */
const heldPatchIds = new Set<string>();
let flushRequest: number | null = null;

function flushPendingPatches(): void {
  const queued = [...pendingPatches];
  const held = queued.filter(([id]) => heldPatchIds.has(id));
  const sent = queued.filter(([id]) => !heldPatchIds.has(id));
  pendingPatches.clear();
  heldPatchIds.clear();
  flushRequest = null;
  if (held.length) {
    withMutationEmitSuppressed(() => applyFramePatches(held, "server"));
  }
  if (sent.length) applyFramePatches(sent, "server");
}

/**
 * Batch server patches (websocket events) per animation frame: later fields win, and every
 * frame's patches land in one frames-store write. A patch queued on a mirror
 * while emit is suppressed is applied suppressed at flush, so a mirror never
 * sends its whole store upstream over the owner's.
 */
export function queueFramePatch(id: string, patch: Partial<Frame>): void {
  if (!holdsDeferredEmit()) heldPatchIds.delete(id);
  else if (!pendingPatches.has(id) || heldPatchIds.has(id)) {
    heldPatchIds.add(id);
  }
  const existing = pendingPatches.get(id);
  pendingPatches.set(id, existing ? { ...existing, ...patch } : patch);
  flushRequest ??= requestAnimationFrame(flushPendingPatches);
}

/** Remove frames from the frames store; their canvas nodes and edges follow. */
export function discardFrames(ids: Iterable<string>): void {
  const { framesById, removeFrames } = useFramesStore.getState();
  const present = [...ids].filter((id) => framesById[id] !== undefined);
  if (present.length === 0) return;
  for (const id of present) stopFrameTerminal(framesById[id]);
  removeFrames(present);
}
